import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, forkJoin, map, Observable, of, Subject } from 'rxjs';
import { SelectedSystem, SystemIBase } from '../types/selectedSystem.types';
import { AutocheckData, EventDetails, MaintenanceDetails, PSMData, SubsystemErrors, SystemHealthOverview } from '../types/systemhealth.types';
import { EscalationsData, NotiDetails, NotificationsData, NotiSiteResponse, PartsData, PsmSiteResponse, ServiceHistoryOverview, SitePartsResponse, SiteVisitsResponse, TimelineResponse, UpcomingServices, VisitsData } from '../types/servicehistory.types';
import { ServiceMetricsOverview } from '../types/servicemetrics.types';
import { FunctionalLocationOverview } from '../types/funcloc.types';
import { LoadingService } from './loading.service';
import { DataService } from './data.service';
import { ApiResponse } from '../types/general.types';
import { AuthService } from './auth.service';
import { SelectedContentService } from './selectedcontent.service';
import { SelectedSiteService } from './selectedsite.service';
import { upcomingserviceData, upcomingserviceSiteData } from '../types/upcomingservicedetails';
import { Title } from '@angular/platform-browser';
import { SubscriptionService } from './subscription.service';
import { environment } from 'src/environments/environment';
import { BusinessPartnerAccessService } from './business-partner-access.service';
import { TranslocoService } from '@jsverse/transloco';

@Injectable({
  providedIn: 'root'
})
export class SelectedsystemService {

  constructor(private title: Title, private loadingService: LoadingService, private dataService: DataService, private authService: AuthService, private selectedContentService: SelectedContentService, private subscriptionService: SubscriptionService, public businessPartnerAccessService: BusinessPartnerAccessService, private translocoService: TranslocoService) { }

  //Reload Selected System
  $reloadSelectedSystem: Subject<void> = new Subject<void>();
  $refreshAutochecks: Subject<void> = new Subject<void>();
  $refreshMaintenance: Subject<void> = new Subject<void>();
  $refreshPSMs: Subject<void> = new Subject<void>();
  $refreshSubsystems: Subject<void> = new Subject<void>();
  $refreshEvents: Subject<void> = new Subject<void>();
  $refreshNotifications: Subject<void> = new Subject<void>();
  $refreshParts: Subject<void> = new Subject<void>();
  $refreshVisits: Subject<void> = new Subject<void>();
  $refreshEscalations: Subject<void> = new Subject<void>();
  $refreshSite: Subject<void> = new Subject<void>();
  $refreshUpcomingServices: Subject<void> = new Subject<void>();

  //Prompts
  $prompts: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);

  //Overview API Results
  $ibaseinformation: BehaviorSubject<SystemIBase> = new BehaviorSubject<SystemIBase>(null);
  $systemhealth: BehaviorSubject<SystemHealthOverview> = new BehaviorSubject<SystemHealthOverview>(null);
  $servicehistory: BehaviorSubject<ServiceHistoryOverview> = new BehaviorSubject<ServiceHistoryOverview>(null);
  $servicemetrics: BehaviorSubject<ServiceMetricsOverview> = new BehaviorSubject<ServiceMetricsOverview>(null);
  $siteoverview: BehaviorSubject<FunctionalLocationOverview> = new BehaviorSubject<FunctionalLocationOverview>(null);
  SitePullDate: Date;
  SitePulledFor: string;

  //Detail API Results
  $maintenance: BehaviorSubject<MaintenanceDetails> = new BehaviorSubject<MaintenanceDetails>(null);
  maintenancePullDate: Date;
  maintenancePulledFor: string;

  $autocheck: BehaviorSubject<AutocheckData> = new BehaviorSubject<AutocheckData>(null);
  autocheckPullDate: Date;
  autocheckPulledFor: string;

  $errors: BehaviorSubject<EventDetails> = new BehaviorSubject<EventDetails>(null);
  errorsPullDate: Date;
  errorsPulledFor: string;

  $selectedSubsystem: Subject<string> = new Subject<string>();
  $subsystemdata: BehaviorSubject<SubsystemErrors> = new BehaviorSubject<SubsystemErrors>([]);
  subsystemPullDate: Date;
  subsystemPulledFor: string;

  $proas: BehaviorSubject<PSMData | PsmSiteResponse> = new BehaviorSubject<PSMData | PsmSiteResponse>(null);
  proasPullDate: Date;
  proasPulledFor: string;

  $selectedPattern: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  $proaDetails: BehaviorSubject<any[]> = new BehaviorSubject<any[]>(null);

  $notifications: BehaviorSubject<NotificationsData | NotiSiteResponse> = new BehaviorSubject<NotificationsData | NotiSiteResponse>(null);
  NotiPullDate: Date;
  notiPulledFor: string;

  $selectedNotification: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  $notificationDetails: BehaviorSubject<NotiDetails[]> = new BehaviorSubject<NotiDetails[]>(null);
  $notificationSummary: BehaviorSubject<TimelineResponse[]> = new BehaviorSubject<TimelineResponse[]>(null);

  $spareparts: BehaviorSubject<PartsData | SitePartsResponse> = new BehaviorSubject<PartsData | SitePartsResponse>(null);
  PartsPullDate: Date;
  partsPulledFor: string;

  $visits: BehaviorSubject<VisitsData | SiteVisitsResponse> = new BehaviorSubject<VisitsData | SiteVisitsResponse>(null);
  VisitsPullDate: Date;
  visitsPulledFor: string;

  $escalations: BehaviorSubject<EscalationsData> = new BehaviorSubject<EscalationsData>(null);
  EscalationsPullDate: Date;
  escalationsPulledFor: string;

  $upcomingservices: BehaviorSubject<UpcomingServices> = new BehaviorSubject<UpcomingServices>(null);
  $upcomingservicedetails: BehaviorSubject<upcomingserviceData | upcomingserviceSiteData> = new BehaviorSubject<upcomingserviceData | upcomingserviceSiteData>(null);
  upcomingPullDate: Date;
  upcomingPulledFor: string;

  private get shouldLoadServiceMetrics(): boolean {
    return environment.enableServiceMetricsWidget;
  }



  //Used to reset widgets for a re-load
  resetForReload() {
    //Reset all saved data
    this.$ibaseinformation.next(null);
    this.$systemhealth.next(null);
    this.$servicehistory.next(null);
    this.$servicemetrics.next(null);
    this.$siteoverview.next(null);
    this.$upcomingservices.next(null);
    this.$upcomingservicedetails.next(null);
    this.$prompts.next([]);

    //Reset Loading States for Widgets
    this.loadingService.$isSystemDetailsLoading.next(true);
    this.loadingService.$isSystemHealthLoading.next(true);
    this.loadingService.$isServiceHistoryLoading.next(true);
    this.loadingService.$isServiceMetricsLoading.next(this.shouldLoadServiceMetrics);
    this.loadingService.$isSiteLoading.next(true);
    this.loadingService.$isPromptsLoading.next(true);
    this.loadingService.$isUpcomingServicesLoading.next(true);

    //Reset Error States
    this.loadingService.$isSystemDetailsError.next(false);
    this.loadingService.$isSystemHealthError.next(false);
    this.loadingService.$isServiceHistoryError.next(false);
    this.loadingService.$isServiceMetricsError.next(false);
    this.loadingService.$isSiteError.next(false);
    this.loadingService.$isPromptsError.next(false);

    //Reset Empty States
    this.loadingService.$isSystemHealthEmpty.next(false);
    this.loadingService.$isServiceHistoryEmpty.next(false);
    this.loadingService.$isServiceMetricsEmpty.next(false);
    this.loadingService.$isSiteEmpty.next(false);
    this.loadingService.$isPromptsError.next(false);
    this.loadingService.$isUpcomingServicesError.next(false);
    this.loadingService.$isUpcomingServicesEmpty.next(false);
  }

  resetAllData() {

    //Reset UI selections
    this.$selectedSubsystem.next(null);
    this.$selectedPattern.next(null);
    this.$selectedNotification.next(null);

    //Reset Main State
    this.$ibaseinformation.next(null);
    this.$systemhealth.next(null);
    this.$servicehistory.next(null);
    this.$servicemetrics.next(null);
    this.$siteoverview.next(null);
    this.$prompts.next([]);

    //Reset Details
    this.$maintenance.next(null);
    this.$autocheck.next(null);
    this.$errors.next(null);
    this.$subsystemdata.next([]);

    this.$notifications.next(null);
    this.$spareparts.next(null);
    this.$visits.next(null);
    this.$escalations.next(null);
    this.$proas.next(null);
    this.$upcomingservices.next(null);
    this.$upcomingservicedetails.next(null);

    //Reset Sub-Detail States
    this.$proaDetails.next(null);
    this.$notificationDetails.next(null);
    this.$notificationSummary.next(null);

    //Reset Cache
    this.SitePullDate = null;
    this.SitePulledFor = null;
    this.maintenancePullDate = null;
    this.maintenancePulledFor = null;
    this.autocheckPullDate = null;
    this.autocheckPulledFor = null;
    this.errorsPullDate = null;
    this.errorsPulledFor = null;
    this.subsystemPullDate = null;
    this.subsystemPulledFor = null;
    this.proasPullDate = null;
    this.proasPulledFor = null;
    this.notiPulledFor = null;
    this.NotiPullDate = null;
    this.partsPulledFor = null;
    this.PartsPullDate = null;
    this.visitsPulledFor = null;
    this.VisitsPullDate = null;
    this.escalationsPulledFor = null;
    this.EscalationsPullDate = null;
    this.upcomingPullDate = null;
    this.upcomingPulledFor = null;
  }

  pullData(selectedmaterial, selectedsystem, selectedlab, type: 'device' | 'site' = 'device') {
    //1. Get IBase Information of selected System
    this.dataService.getIBaseInformation(selectedmaterial, selectedsystem).subscribe({
      next: ({ response, status }: ApiResponse<SystemIBase>) => {

        if (status === 204) {
          this.loadingService.$isSystemDetailsEmpty.next(true);
        }
        else if (status === 200) {
          this.$ibaseinformation.next(response);
        }

        this.loadingService.$isSystemDetailsLoading.next(false);
      },
      error: (error) => {
        this.loadingService.$isSystemDetailsError.next(true);
        this.loadingService.$isSystemDetailsLoading.next(false);
      }
    });

    //2. Get System Health Overview
    if(this.businessPartnerAccessService.canViewMachineData || !environment.businessPartnerMode) {
    this.dataService.getSystemHealthOverview(selectedmaterial, selectedsystem).subscribe({
      next: ({ response, status }: ApiResponse<SystemHealthOverview>) => {

        if (status === 204) {
          this.loadingService.$isSystemHealthEmpty.next(true);
        }
        else if (status === 200) {
          this.$systemhealth.next(response);
        }
        this.loadingService.$isSystemHealthLoading.next(false);
      },
      error: (error) => {
        this.loadingService.$isSystemHealthError.next(true);
        this.loadingService.$isSystemHealthLoading.next(false);
      }
    });
    }

    //3. Get Service History Overview
    if(this.businessPartnerAccessService.canViewServiceData || !environment.businessPartnerMode) {
    this.dataService.getServiceHistoryOverview(selectedmaterial, selectedsystem).subscribe({
      next: ({ response, status }: ApiResponse<ServiceHistoryOverview>) => {
        if (status === 204) {
          this.loadingService.$isServiceHistoryEmpty.next(true);
        }
        else if (status === 200) {
          this.$servicehistory.next(response);
        }
        this.loadingService.$isServiceHistoryLoading.next(false);
      },
      error: (error) => {
        this.loadingService.$isServiceHistoryError.next(true);
        this.loadingService.$isServiceHistoryLoading.next(false);
      }
    });
  }

    //4. Get Service Metrics Overview
    if (this.shouldLoadServiceMetrics) {
      this.dataService.getServiceMetricsOverview(selectedmaterial, selectedsystem).subscribe({
        next: ({ response, status }: ApiResponse<ServiceMetricsOverview>) => {
          if (status === 204) {
            this.loadingService.$isServiceMetricsEmpty.next(true);
          }
          else if (status === 200) {
            this.$servicemetrics.next(response);
          }
          this.loadingService.$isServiceMetricsLoading.next(false);
        },
        error: (error) => {
          this.loadingService.$isServiceMetricsError.next(true);
          this.loadingService.$isServiceMetricsLoading.next(false);
        }
      });
    } else {
      this.$servicemetrics.next(null);
      this.loadingService.$isServiceMetricsLoading.next(false);
      this.loadingService.$isServiceMetricsError.next(false);
      this.loadingService.$isServiceMetricsEmpty.next(false);
    }

    //5. Get Site Overview
    this.dataService.getFunctionalLocationOverview(selectedlab).subscribe({
      next: ({ response, status }: ApiResponse<any>) => {
        if (status === 204) {
          this.loadingService.$isSiteEmpty.next(true);
        }
        else if (status === 200) {
          this.$siteoverview.next(response);
        }
        this.loadingService.$isSiteLoading.next(false);
      },
      error: (error) => {
        this.loadingService.$isSiteError.next(true);
        this.loadingService.$isSiteLoading.next(false);
      }
    });

    //6. Get Prompts
    this.dataService.getPrompts(selectedmaterial, selectedsystem).subscribe({
      next: (data: any) => {
        this.$prompts.next(data?.response?.prompts || []);
        this.loadingService.$isPromptsLoading.next(false);
      },
      error: (error) => {
        console.error("Error fetching Service Metrics information:", error);
        this.loadingService.$isPromptsError.next(true);
      }
    });

    //7. Upcoming Services - Device
    if (type === 'device' && (this.businessPartnerAccessService.canViewServiceData || !environment.businessPartnerMode)) {
      this.dataService.getUpcomingServicesOverview(selectedmaterial, selectedsystem).subscribe({
        next: ({ response, status }: ApiResponse<UpcomingServices>) => {
          if (status === 204) {
            this.loadingService.$isUpcomingServicesEmpty.next(true);
          }
          else if (status === 200) {
            this.$upcomingservices.next(response);
          }
          this.loadingService.$isUpcomingServicesLoading.next(false);
        },
        error: (error) => {
          this.loadingService.$isUpcomingServicesError.next(true);
          this.loadingService.$isUpcomingServicesLoading.next(false);
        }
      });

      // this.dataService.getUpcomingServicesDetails(selectedmaterial, selectedsystem).subscribe({
      //   next: ({ response, status }: ApiResponse<upcomingserviceData>) => {
      //     if (status === 204) {
      //       this.loadingService.$isUpcomingServiceDetailsEmpty.next(true);
      //     }
      //     else if (status === 200) {
      //       this.$upcomingservicedetails.next(response);
      //     }
      //     this.loadingService.$isUpcomingServiceDetailsLoading.next(false);
      //   },
      //   error: (error) => {
      //     this.loadingService.$isUpcomingServiceDetailsError.next(true);
      //     this.loadingService.$isUpcomingServiceDetailsLoading.next(false);
      //   }
      // });
    }

    //8. Upcoming Services - Site
    if (type === 'site' && (this.businessPartnerAccessService.canViewServiceData || !environment.businessPartnerMode)) {
      this.dataService.getUpcomingServicesSiteOverview(selectedlab).subscribe({
        next: ({ response, status }: ApiResponse<UpcomingServices>) => {
          if (status === 204) {
            this.loadingService.$isUpcomingServicesEmpty.next(true);
          }
          else if (status === 200) {
            this.$upcomingservices.next(response);
          }
          this.loadingService.$isUpcomingServicesLoading.next(false);
        },
        error: (error) => {
          this.loadingService.$isUpcomingServicesError.next(true);
          this.loadingService.$isUpcomingServicesLoading.next(false);
        }
      });

      // this.dataService.getUpcomingServicesSiteDetails(selectedlab).subscribe({
      //   next: ({ response, status }: ApiResponse<upcomingserviceData>) => {
      //     if (status === 204) {
      //       this.loadingService.$isUpcomingServiceDetailsEmpty.next(true);
      //     }
      //     else if (status === 200) {
      //       this.$upcomingservicedetails.next(response);
      //     }
      //     this.loadingService.$isUpcomingServiceDetailsLoading.next(false);
      //   },
      //   error: (error) => {
      //     this.loadingService.$isUpcomingServiceDetailsError.next(true);
      //     this.loadingService.$isUpcomingServiceDetailsLoading.next(false);
      //   }
      // });
    }


  }






  setNewDevice(material: string, serial: string): Observable<SelectedSystem | null> {

    if (serial + material == this.selectedContentService.currentSystem?.SerialNumber + this.selectedContentService.currentSystem?.MaterialNumber) {
      return of(null);
    }

    this.selectedContentService.clear();
    this.resetForReload();
    this.selectedContentService.$isLoadingContent.next(true);

    return forkJoin([
      this.dataService.searchExact(material, serial),
      this.dataService.accessDevice({
        "email": this.authService.userData?.basic["mail"],
        "serial_number": serial,
        "material_number": material
      })
    ]).pipe(
      map(([exactSystemResult, accessDeviceResult]) => {
        const { response } = exactSystemResult;

        //No Support
        if (response?.Support === "None") {
          const entry = {
            id: "unsupported-device" + crypto.randomUUID(),
            type: "warning",
            title: this.translocoService.translate('home.notification.unsupportedDevice.title'),
            message: this.translocoService.translate('home.notification.unsupportedDevice.message'),
            timestamp: new Date().toISOString(),
            read: false
          };

          const current = this.subscriptionService.$notifications.getValue();
          const currentCenter = this.subscriptionService.$notificationsCenter.getValue();

          this.subscriptionService.$notifications.next([...current, entry]);
          this.subscriptionService.$notificationsCenter.next([...currentCenter, entry]);

          //Stop Content Loading
          this.selectedContentService.$isLoadingContent.next(false);

          return null;
        }

        //No access
        if (!accessDeviceResult?.response) {
          const entry = {
            id: "unauthorized-device" + crypto.randomUUID(),
            type: "warning",
            title: this.translocoService.translate('home.notification.unauthorizedDevice.title'),
            message: this.translocoService.translate('home.notification.unauthorizedDevice.message'),
            timestamp: new Date().toISOString(),
            read: false
          };

          const current = this.subscriptionService.$notifications.getValue();
          const currentCenter = this.subscriptionService.$notificationsCenter.getValue();

          this.subscriptionService.$notifications.next([...current, entry]);
          this.subscriptionService.$notificationsCenter.next([...currentCenter, entry]);

          //Stop Content Loading
          this.selectedContentService.$isLoadingContent.next(false);

          return null;
        }

        let newDevice: SelectedSystem = {
          'SerialNumber': response.SERIAL_NUMBER,
          'MaterialNumber': response.MATERIAL_NUMBER,
          'SupportType': response.Support,
          'LabName': response.FUNCTIONAL_LOCATION,
          'Functional_Location_ID': response.Functional_Location_ID,
          'ProductFamily': response.ProductFamily
        };

        this.selectedContentService.selectDevice(newDevice);

        //Change Browswer Title
        this.title.setTitle(newDevice.SerialNumber + ' - AXIS');

        return newDevice;
      }),
      catchError(() => {
        //Stop Content Loading
        this.selectedContentService.$isLoadingContent.next(false);
        return of(null);
      })
    );
  }


}